// let's imagine this file is autogenerated from the backend
// ideally, we want to keep these api related types in sync
// with the backend instead of manually writing them out

import { Database, Enums } from '@/lib/supabase';

export type BaseEntity = {
  id: string;
  createdAt: number;
};

export type Entity<T> = {
  [K in keyof T]: T[K];
} & BaseEntity;

export type Meta = {
  page: number;
  total: number;
  totalPages: number;
};

export type User = Entity<{
  firstName: string;
  lastName: string;
  email: string;
  role: 'ADMIN' | 'USER';
  teamId: string;
  bio: string;
}>;

export type OrganizationCategory = Row<'organization_categories'>;

export type Profile = Row<'profiles'>;

export type AuthResponse = {
  jwt: string;
  user: User;
};

export type Team = Entity<{
  name: string;
  description: string;
}>;

export type Discussion = Entity<{
  title: string;
  body: string;
  teamId: string;
  author: User;
}>;

export type Comment = Entity<{
  body: string;
  discussionId: string;
  author: User;
}>;

export enum KpiType {
  NOMINAL = 'nominal',
  RELATIVE = 'relative',
}

export type KpiForecast = Row<'kpi_forecasts'>;

export type Org = Row<'orgs'>;

export type Project = Row<'projects'>;
export type ProjectWithOrg = Project & {
  org: Org | null;
};
export type ProjectWithParent = Project & {
  parent: ProjectWithOrg | null;
};
export type ProjectWithOrgAndParent = Project & {
  org: Org | null;
  parent: ProjectWithOrg | null;
};
export type ProjectKpi = Row<'project_kpis'> & {
  kpi_budget_id?: string;
};
export type DerivativeKpi = Row<'derivative_kpis'> & {
  kpi_budget_id?: undefined;
};

// type to mark fileds that are not nullable
type PartialNullBy<T, E extends keyof T> = {
  [K in keyof T]: K extends E ? T[K] | null : T[K];
};

type AllRequired<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

export type ProjectKpiMetrics = PartialNullBy<
  AllRequired<ViewRow<'kpi_metrics_view'>>,
  | 'latest_entry_value'
  | 'second_latest_entry_value'
  | 'last_entry_date'
  | 'aggregated_previous_value'
>;

export type ProjectKpiWithMetrics = ProjectKpi & {
  metrics?: ProjectKpiMetrics;
};

export type DerivativeKpiMetrics = PartialNullBy<
  AllRequired<ViewRow<'derivative_kpi_metrics_view'>>,
  'latest_entry_value' | 'second_latest_entry_value' | 'last_entry_date'
>;

export type KpiWithMetrics = ProjectKpiWithMetrics | DerivativeKpiWithMetrics;

export type DerivativeKpiWithMetrics = DerivativeKpi & {
  metrics?: DerivativeKpiMetrics;
};
export type CreateProjectKpiInsert =
  Database['public']['Tables']['project_kpis']['Insert'] & {
    project_id: number;
  };

export type CreateDerivativeKpiInsert =
  Database['public']['Tables']['derivative_kpis']['Insert'];

export type CreateProjectKpi = Omit<CreateProjectKpiInsert, 'project_id'>;

export type CreateDerivativeKpi = Omit<CreateDerivativeKpiInsert, 'project_id'>;

export type ProjectWithContractors = Project & {
  contractors: (SubprojectContractor & PartnerAlias)[];
  last_entry_date: string | null;
  unread_notification_count: number;
  is_overdue: boolean;
};

export type SubprojectContractor = Row<'project_contractors'>;
export type PartnerAlias = Row<'partner_aliases'>;
export type ProjectType = Enums<'project_type'>;
export type ContractorWithAlias = SubprojectContractor & {
  partner_alias: PartnerAlias & {
    partner_org: Org | null;
  };
};
export type SubprojectKpi = Row<'subproject_view_budget'>;
export type SubprojectBudgetInsert =
  Database['public']['Tables']['subproject_view_budget']['Insert'];
export type SubprojectBudgetUpdate =
  Database['public']['Tables']['subproject_view_budget']['Update'];

export type KpiTrackingEntry = Row<'kpi_tracking_entries'>;

export type KpiWithEntries = (ProjectKpi | DerivativeKpi) & {
  entries: KpiTrackingEntry[] | undefined;
};

export type InvitationStatus = Enums<'invitation_status'>;

// Documents
export type ProjectDocument = Row<'project_documents'>;
export type SecureProjectDocument = Omit<ProjectDocument, 'creator_id'> & {
  creator: {
    id: string;
    name: string;
  };
  write_access: boolean;
  project_name: string | null;
  sub_project_names: string | null;
  partner_names: string | null;
  role: string;
  uploaded_by_org: string | null;
  project_type: 'project' | 'subproject_client' | 'subproject_contractor';
  client_org_name: string | null;
};
export type ProjectDocumentInsertDto = InsertDto<'project_documents'>;
export type ProjectDocumentUpdateDto = UpdateDto<'project_documents'>;

export type SubprojectComment = Row<'subproject_comment'>;
export type SubprojectCommentWithAuthor = SubprojectComment & {
  author: Profile;
};
export type ProjectInvite = Row<'project_member_invites'>;

export type SubprojectInvitation = Row<'contractor_subproject_invitation'>;

// DTOs
export type Row<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Row'];

export type ViewRow<T extends keyof Database['public']['Views']> =
  Database['public']['Views'][T]['Row'];
export type InsertDto<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Insert'];
export type UpdateDto<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];
export type FunctionReturn<T extends keyof Database['public']['Functions']> =
  Database['public']['Functions'][T]['Returns'];

export type ProjectSubscription = Row<'project_subscriptions'>;
export type SubscriptionAddon = Row<'subscription_addons'>;
export type AddonSubscriptionRow = Row<'subscription_addons'>;

export type SubscriptionStatus = Enums<'stripe_status'>;
export const activeSubscriptionStatuses: readonly SubscriptionStatus[] = [
  'active',
  'trialing',
];

export type ProjectSubscriptionWithEntitlementsAndUsage =
  Database['public']['CompositeTypes']['project_subscription_with_entitlements_and_usage'];

export type DashboardProjectSlots = Row<'dashboard_project_slots'>;
export type ApiUpdateDashboardProjectSlotsInput =
  UpdateDto<'dashboard_project_slots'>;
